import React, { useState } from 'react';
import { Box, Typography, LinearProgress, Button, IconButton, Dialog } from "@mui/material";
import HelpIcon from '@mui/icons-material/Help';
import ScoreCircularProgressV2, { getLinearGradientByScore } from "src/common/config/ScoreCircularProgressV2";
import { CustomTooltip } from "src/content/keywordResearch/CustomTooltip";
import { newCardsCss } from "src/content/dashboard/DashboardCss";
import { keywordResearchDashBoardCss } from "src/content/keywordResearch/keywordResearchCSS";
import { KeywordOverviewResultCardsCss } from "src/content/keywordResearch/keywordOverview/keywordOverviewCSS";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { GBPHealthSection } from '../types';
import { usePagination } from '../../content-strategy/hooks/usePagination';


export interface IGoogleBusinessCard {
  healthScore: {
    value: number;
    change: number
  }
  sections: GBPHealthSection[]
}

const statusStyles: Record<string, { text: string; bg: string }> = {
  Excellent: { text: "#067647", bg: "#ECFDF3" },
  Good: { text: "#027A48", bg: "#ECFDF3" },
  Fair: { text: "#B54708", bg: "#FFFAEB" },
  Poor: { text: "#B42318", bg: "#FEF3F2" }
};

export default function GoogleBusinessCard(props: IGoogleBusinessCard) {

  const [selectedSection, setSelectedSection] = useState<GBPHealthSection | null>(null);

  const {
    currentItems: displayedItems,
    page,
    totalPages,
    handlePrev,
    handleNext,
    isFirstPage,
    isLastPage
  } = usePagination(props.sections || [], 4);

  return (
    <div className="bg-white border border-[#EAECF0] rounded-xl shadow-sm p-6 w-full pb-0">
      <div className="flex justify-between items-center mb-4">
        <Box className="flex items-center gap-3">
          <Typography sx={newCardsCss?.headTextCss}>
            Google Business
          </Typography>
          <CustomTooltip
            title={
              <Box>
                <Typography
                  sx={keywordResearchDashBoardCss?.customTollTipText}
                >
                  Overview of your Google Business Profile, including listing details, categories, and optimization status.
                </Typography>
              </Box>
            }
            placement="bottom"
            arrow
          >
            <HelpIcon sx={KeywordOverviewResultCardsCss?.helpIcon} />
          </CustomTooltip>
        </Box>

        <div className='flex items-center'>
          <p className="text-primary text-base font-medium">
            {page}/{totalPages}
          </p>
          <IconButton
            onClick={handlePrev}
            disabled={isFirstPage}
            size="small"
            sx={{ p: 1 }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 16, color: isFirstPage ? '#CBD5E1' : 'var(--color-primary)' }} />
          </IconButton>
          <IconButton
            onClick={handleNext}
            disabled={isLastPage}
            size="small"
            sx={{ p: 1 }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 16, color: isLastPage ? '#CBD5E1' : 'var(--color-primary)' }} />
          </IconButton>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row items-center gap-8">

        {/* Health Score Progress Circle */}
        <div className="flex-shrink-0 flex items-center justify-center w-full lg:w-fit lg:pr-8">
          <ScoreCircularProgressV2 value={props.healthScore.value}>
            <ScoreCircularProgressV2.Title title='Visibility' className='' />
            <ScoreCircularProgressV2.Value value={props.healthScore.value} className='' scoreClass='!text-3xl' />
            {/* <ScoreCircularProgressV2.Trend trendValue={5} className='mt-1' /> */}
          </ScoreCircularProgressV2>
        </div>

        {/* Other Four Stats */}
        <div className="w-full">
          {displayedItems.map((info, index) => (
            <div
              key={index}
              className={
                [
                  `py-5 grid grid-cols-2 md:grid-cols-[0.5fr_2fr_1fr] gap-6 flex-grow w-full`,
                  index === displayedItems.length - 1 ? "" : "border-b border-[#E0E0E6]"
                ].join(" ")
              }
            >
              <div className="flex items-center">
                <p className="text-[#2B2F36] font-semibold text-base">
                  {info.name}
                </p>
              </div>
              <div>
                <div className="flex justify-between">
                  <p className="text-primary font-medium text-sm">Score</p>
                  <p className="text-primary font-bold text-sm">{info.score}</p>
                </div>
                <LinearProgress
                  value={info.score}
                  variant="determinate"
                  className={`w-full`}
                  sx={{
                    borderRadius: '20px',
                    height: '8px',
                    bgcolor: "#F2F4F7", // dynamic background
                    '& .MuiLinearProgress-bar': {
                      borderRadius: '20px',
                      background: getLinearGradientByScore(info.score)
                    }
                  }}
                />
              </div>
              <div className="flex items-center justify-end gap-3">
                {(() => {
                  const style = statusStyles[info.status] || { text: "#B42318", bg: "#FEF3F2" };
                  return (
                    <p
                      className="rounded-2xl text-xs font-medium px-2 py-0.5"
                      style={{ color: style.text, backgroundColor: style.bg }}
                    >
                      {info.status}
                    </p>
                  );
                })()}
                <Button
                  variant="outlined"
                  className="px-2 h-10 !border-[#CFCFD9] !text-[#120242] !font-medium"
                  onClick={() => setSelectedSection(info)}
                >
                  Details
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog
        open={Boolean(selectedSection)}
        onClose={() => setSelectedSection(null)}
        disableScrollLock
        maxWidth="md"
        fullWidth
      >
        {selectedSection && (
          <div className="p-6 relative max-h-[85vh] overflow-y-auto">
            {/* Close button */}
            <div aria-label="dialog-close-btn" className="absolute top-4 right-4">
              <IconButton
                onClick={() => setSelectedSection(null)}
                sx={{
                  color: '#98A2B3',
                  '&:hover': {
                    backgroundColor: '#F2F4F7',
                    borderRadius: '8px'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>

            {/* Header */}
            <div className="mb-6">
              <h2 className="text-[#101828] font-bold text-2xl mb-2 flex items-center gap-3">
                {selectedSection.name}
              </h2>
              <div className="flex flex-wrap gap-1 bg-[#F9FAFB] border border-[#EAECF0] rounded-lg p-3 mt-3">
                <div className="flex items-center gap-1.5 bg-white border border-[#F2F4F7] rounded-md px-3 py-2 whitespace-nowrap">
                  <span className="text-[#475467] text-xs font-semibold uppercase tracking-wider">Score:</span>
                  <span className="text-[#101828] text-sm font-medium">{selectedSection.score}/100</span>
                </div>
                <div className="flex items-center gap-1.5 bg-white border border-[#F2F4F7] rounded-md px-3 py-2 whitespace-nowrap">
                  <span className="text-[#475467] text-xs font-semibold uppercase tracking-wider">Weight:</span>
                  <span className="text-[#101828] text-sm font-medium">{selectedSection.weight}%</span>
                </div>
                <div className="flex items-center gap-1.5 bg-white border border-[#F2F4F7] rounded-md px-3 py-2 whitespace-nowrap">
                  <span className="text-[#475467] text-xs font-semibold uppercase tracking-wider">Status:</span>
                  {(() => {
                    const style = statusStyles[selectedSection.status] || { text: "#B42318", bg: "#FEF3F2" };
                    return (
                      <span
                        className="rounded-2xl inline text-xs px-2.5 py-0.5 font-medium"
                        style={{ color: style.text, backgroundColor: style.bg }}
                      >
                        {selectedSection.status}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Content: side-by-side layout */}
            <div className={`flex gap-6 mt-6 ${selectedSection.issues.length === 0 ? 'flex-col-reverse' : 'flex-col'}`}>
              {/* Left Pane: Issues */}
              <div className="flex flex-col gap-2">
                <h3 className="text-[#344054] font-semibold text-lg border-b border-[#EAECF0] pb-1">
                  Issues ({selectedSection.issues.length})
                </h3>
                {selectedSection.issues.length === 0 ? (
                  <div className="flex items-center gap-2 text-[#027A48] bg-[#ECFDF3] border border-[#D1FADF] rounded-lg p-3 text-sm font-medium">
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                    No issues detected. Looking good!
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1 bg-[#FEF3F2] border border-[#FEE4E2] rounded-lg p-3 max-h-[450px] overflow-y-auto">
                    {selectedSection.issues.map((issue, idx) => {
                      const isLongIssue = typeof issue === 'string' && issue.length > 60;
                      if (isLongIssue) {
                        return (
                          <div key={idx} className="w-full flex items-start gap-2 bg-white border border-[#FEE4E2] rounded-md p-2.5">
                            <ErrorOutlineIcon className="text-[#D92D20] shrink-0 mt-0.5" sx={{ fontSize: 18 }} />
                            <span className="text-[#B42318] text-sm font-medium break-words whitespace-pre-wrap">{issue}</span>
                          </div>
                        );
                      }
                      return (
                        <div key={idx} className="flex items-center gap-1.5 bg-white border border-[#FEE4E2] rounded-md px-3 py-2 whitespace-nowrap max-w-full">
                          <ErrorOutlineIcon className="text-[#D92D20] shrink-0" sx={{ fontSize: 18 }} />
                          <span className="text-[#B42318] text-sm font-medium">{issue}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
               {/* Right Pane: Suggestions */}
              <div className="flex flex-col gap-2">
                <h3 className="text-[#344054] font-semibold text-lg border-b border-[#EAECF0] pb-1">
                  Suggestions
                </h3>
                <div className="flex flex-wrap gap-1 bg-[#F9FAFB] border border-[#EAECF0] rounded-lg p-3">
                  {selectedSection.details.map((detail, idx) => {
                    const isLongText = typeof detail.value === 'string' && detail.value.length > 60;
                    const isArray = Array.isArray(detail.value);
                    if (isLongText) {
                      return (
                        <div key={idx} className="w-full flex flex-col gap-1.5 bg-white border border-[#F2F4F7] rounded p-2.5">
                          <span className="text-[#475467] text-xs font-semibold uppercase tracking-wider">{detail.label}</span>
                          <div className="text-[#101828] text-sm break-words">
                            {detail.value}
                          </div>
                        </div>
                      );
                    }
                    if (isArray) {
                      return (
                        <div key={idx} className="w-full flex flex-col gap-1.5">
                          <span className="text-[#475467] text-xs font-semibold uppercase tracking-wider">{detail.label}</span>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {detail.value.length === 0 ? (
                              <span className="text-[#98A2B3] text-sm italic">None</span>
                            ) : (
                              detail.value.map((item: any, itemIdx: number) => {
                                const displayName = typeof item === 'object' && item !== null 
                                  ? item.name || JSON.stringify(item) 
                                  : item;
                                return (
                                  <span key={itemIdx} className="bg-white border border-[#D0D5DD] text-[#344054] text-xs px-2 py-1 rounded-md font-medium whitespace-nowrap">
                                    {displayName}
                                  </span>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={idx} className="flex items-center gap-1.5 bg-white border border-[#F2F4F7] rounded-md px-3 py-2 whitespace-nowrap max-w-full">
                        <span className="text-[#475467] text-xs font-semibold uppercase tracking-wider">{detail.label}:</span>
                        <span className="text-[#101828] text-sm font-medium whitespace-nowrap">
                          {detail.value === true ? 'Yes' : detail.value === false ? 'No' : String(detail.value ?? 'None')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
